import { Sparkles } from "lucide-react";
import posthog from "posthog-js";
import { useMemo } from "react";

import { Button } from "../button";

import { PLANS, Plan, getPlanFeatures } from "./constants";
import { PlanCard } from "./PlanCard";
import { geIsDowngrade } from "./utils";

import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useConfirm } from "@/context/confirm-context";
import { useOrganizationSubscriptionData } from "@/providers/OrganizationSubscriptionProvider";
import { getActiveOrganizationMembers, useActiveOrgMembers } from "@/queries/organization-members";
import {
  useCancelSubscription,
  useChangePlan,
  useCreateCheckoutSession,
} from "@/queries/organizations";

interface SubscriptionStatusBannerProps {
  showPricingModal: boolean;
  setShowPricingModal: (value: boolean) => void;
  buttonText?: string;
  buttonClassName?: string;
  disabled?: boolean;
  allowWithPaymentIssue?: boolean;
}

export const SubscriptionStatusBanner = ({
  showPricingModal,
  setShowPricingModal,
  buttonText,
  buttonClassName,
  disabled,
  allowWithPaymentIssue = false,
}: SubscriptionStatusBannerProps) => {
  const { data: members } = useActiveOrgMembers({
    perPage: 250,
    page: 1,
  });
  const { mutate: createCheckoutSession, isLoading: isLoadingCreateCheckoutSession } =
    useCreateCheckoutSession();
  const { mutate: cancelSubscription, isLoading: isLoadingCancelSubscription } =
    useCancelSubscription();
  const { mutate: changePlan, isLoading: isLoadingChangePlan } = useChangePlan();
  const { hasLegacySubscription, hasActiveSubscription, subscriptionStatus, hasPaymentIssue } =
    useOrganizationSubscriptionData();

  const totalCurrentMembers = getActiveOrganizationMembers(members?.items).length;
  const isPaymentBlocked = hasPaymentIssue && !allowWithPaymentIssue;
  const isBannerPermissionDisabled = Boolean(disabled) && !hasPaymentIssue;

  const isLoading = useMemo(
    () => isLoadingCreateCheckoutSession || isLoadingCancelSubscription || isLoadingChangePlan,
    [isLoadingCreateCheckoutSession, isLoadingCancelSubscription, isLoadingChangePlan],
  );

  const onPlanSelect = async (plan: Plan) => {
    try {
      if (plan.name === "Free") {
        cancelSubscription(undefined, {
          onSuccess: () => {
            window.location.reload();
          },
        });
      } else {
        if (hasActiveSubscription || hasLegacySubscription) {
          changePlan(
            {
              new_plan: plan.name.toLowerCase() as "pro" | "team",
            },
            {
              onSuccess: () => {
                window.location.reload();
              },
            },
          );

          return;
        }

        createCheckoutSession(
          {
            plan: plan.name.toLowerCase(),
            success_url: window.location.href,
            cancel_url: window.location.href,
          },
          {
            onSuccess: result => {
              posthog.people.set({ started_checkout: true });
              window.location.href = result.checkout_session_url;
            },
          },
        );
      }
    } catch {
      // eslint-disable-next-line no-console
      console.error("Error handling plan select");
    }
  };

  const { confirm } = useConfirm();

  const onPlanButtonClick = (plan: Plan) => {
    // Prevent plan selection when there's a payment issue, unless explicitly allowed
    if (hasPaymentIssue && !allowWithPaymentIssue) {
      return;
    }

    const isTrial = subscriptionStatus?.subscription.status === "trialing";
    const isDowngrade = geIsDowngrade(
      subscriptionStatus?.subscription.plan_type ?? "",
      plan.name.toLowerCase(),
    );

    if (isDowngrade && !isTrial) {
      confirm({
        message: `Are you sure you want to downgrade to the ${plan.name} plan?`,
      }).then(result => {
        if (result) {
          onPlanSelect(plan);
        }
      });

      return;
    }

    if (!isLoading) {
      if (plan.name === "Enterprise") {
        window.open("mailto:hello@promptlayer.com?subject=Enterprise Plan Inquiry");
      } else {
        onPlanSelect(plan);
      }
    }
  };

  return (
    <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
      <DialogTrigger disabled={disabled} asChild>
        <Button
          variant="secondaryLight"
          size="sm"
          disabled={disabled}
          onClick={() => setShowPricingModal(true)}
          className={`group relative gap-2 overflow-hidden px-3 text-xs font-medium transition-all ${buttonClassName}`}
        >
          <Sparkles className="relative z-10 h-3 w-3 transition-transform group-hover:scale-110" />
          <span className="relative z-10">{buttonText || "Upgrade"}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[1240px]">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Choose Your Plan</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {PLANS.map(plan => {
            const features = getPlanFeatures(plan.name);
            const isCurrentPlan =
              !hasLegacySubscription &&
              subscriptionStatus?.subscription.plan_type === plan.name.toLowerCase() &&
              subscriptionStatus?.subscription.status !== "trialing";
            const isTrial = subscriptionStatus?.subscription.status === "trialing";
            const maxUsers =
              features.Users === "Unlimited" ? 999999 : Number(features.Users as string);
            const isMaxUsersReached = totalCurrentMembers > maxUsers;

            return (
              <PlanCard
                key={plan.name}
                plan={plan}
                subscriptionStatus={subscriptionStatus!}
                features={features}
                isLoading={isLoading}
                disabled={disabled || isMaxUsersReached || isPaymentBlocked}
                hasLegacySubscription={hasLegacySubscription}
                isTrialing={isTrial}
                isCurrentPlan={isCurrentPlan}
                onButtonClick={() => onPlanButtonClick(plan)}
                totalCurrentMembers={totalCurrentMembers}
                maxUsersForPlan={maxUsers}
                isMaxUsersReached={isMaxUsersReached}
                isPaymentBlocked={isPaymentBlocked}
                isBannerPermissionDisabled={isBannerPermissionDisabled}
              />
            );
          })}
        </div>
        <div className="text-center text-xs text-gray-500">
          Questions?{" "}
          <a href="mailto:hello@promptlayer.com" className="text-blue-600 hover:text-blue-500">
            Contact us
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
};
